import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  QUESTION_TYPES,
  readInstantSurvey,
  SURVEY_GROUP_VALIDATION_ERROR_HEADER,
  SURVEY_GROUP_VALIDATION_ERROR_TEXT,
  SURVEY_GROUP_VALIDATION_STATUS,
  SURVEY_VALIDATION_ERROR_HEADER,
  SURVEY_VALIDATION_ERROR_TEXT,
  validateInstantSurvey,
} from '@api/instant_survey';

import { publishAtlasSurvey } from '@api/surveys';
import {
  Accordion,
  Announcement,
  Button,
  Card,
  Error,
  formatData,
  getFormattedLongDate,
  Layout,
  LoaderView,
  parseLocalDate,
  Pill,
  Tag,
} from '@utilities';
import {
  CheckCircleSolidIcon,
  ExclamationTriangleIcon,
  FlowArrowIcon,
  InfoFilledIcon,
  TargetIcon,
} from '@utilities/icons';

import Cart from '../../../Checkout/components/Cart';
import GroupAdvancedPrompts from '../Panelists/components/QuotaGroupCard/components/GroupAdvancedPrompts';
import GroupMadlib from '../Panelists/components/QuotaGroupCard/components/GroupMadlib';
import ModalTitleRequired from '../ModalTitleRequired';
import PromoCredits from './components/PromoCredits';
import SurveyToolbar from '../SurveyToolbar';

import { getQuestionCost, isContinuouslyFed } from '../../utilities/helpers';
import { payWithCompletes, payWithCredits } from '../../../Checkout/utilities/constants';
import {
  getCartCost,
  getDisplayCurrency,
} from '../../../Checkout/components/Cart/utilities/helpers';
import {
  formatAvailablePanelists,
  getCurrentQuestionnairePrice,
  getCurrentSamplePrice,
} from '../../utilities/helpers';
import { SurveyContext } from '../../utilities/context';

import styles from './_index.module.scss';

const Review = ({ onPublish, setActiveStep }) => {
  const {
    currentQuestionCount,
    insightsDocument,
    insightsDocumentType,
    instantSurveyType,
    isLoadingSubscription,
    isResearchObjectivesTooLong,
    launchMethod,
    saveInstantSurvey,
    subscription,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);
  const { firstName, lastName, surveyInfo } = useSelector((state) => state?.user?.data || {});
  const [promoCreditsAtReview] = useState(surveyInfo?.promoCredits);
  const [userCreditsAtReview] = useState(surveyInfo?.userCredits);

  const [error, setError] = useState(null);
  const [isTitleRequiredModalOpen, setIsTitleRequiredModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);

  const [addOns, setAddOns] = useState(insightsDocumentType?.instantSurveyAddons || []);
  const [usePromoCredits, setUsePromoCredits] = useState(false);

  const controllerRef = useRef(null);
  if (!controllerRef.current) controllerRef.current = new AbortController();
  const signal = controllerRef.current.signal;

  const surveyIdRef = useRef(null);
  const surveysResponsesValidation = useSelector((state) => state?.websocket?.surveyValidation);
  const { ERROR, NOT_ENOUGH_SAMPLE, NOT_VALIDATED, VALIDATING } = SURVEY_GROUP_VALIDATION_STATUS;

  const hasMaxDiffQuestion = survey.questions.some(
    ({ type }) => type === QUESTION_TYPES.MAX_DIFF.value
  );
  const hasTurfQuestion = survey.questions.some(({ type }) => type === QUESTION_TYPES.TURF.value);
  const isPlan = launchMethod === payWithCompletes.key;

  const getQuestionnaireCompletes = (questions) => {
    if (questions >= 16 && questions <= 20) return 100;
    return 0;
  };
  const questionnairePrice = useMemo(
    () =>
      getCurrentQuestionnairePrice({
        questionnairePricing: insightsDocumentType.questionnairePricing,
        survey,
      }),
    [insightsDocumentType.questionnairePricing, survey]
  );
  const samplePrice = useMemo(
    () => getCurrentSamplePrice({ samplePricing: insightsDocumentType.samplePricing, survey }),
    [insightsDocumentType.samplePricing, survey]
  );
  const totalResponsesRequested = survey.groups.reduce(
    (acc, { quotaLimit }) => acc + quotaLimit,
    0
  );

  const isSurveyContinuouslyFed = useMemo(() => {
    return survey.groups.some((group) => isContinuouslyFed(group));
  }, [survey.groups]);

  const surveyHasNotEnoughSample = useMemo(() => {
    return survey.groups.some(({ validationStatus }) => validationStatus === NOT_ENOUGH_SAMPLE);
  }, [survey.groups]);

  const items = [
    {
      description:
        `${totalResponsesRequested} expected completes` +
        (survey.groups.length > 1
          ? `, ${survey.groups.length} quota group${survey.groups.length > 1 ? 's' : ''}`
          : ''),
      name: 'Survey Sample',
      prices: {
        completes: totalResponsesRequested,
        credits: samplePrice?.amount,
      },
    },
    {
      description: questionnairePrice?.description,
      name: 'Questionnaire Length',
      prices: {
        completes: getQuestionnaireCompletes(questionnairePrice?.questions),
        credits: questionnairePrice?.amount,
      },
    },
    ...(hasTurfQuestion || hasMaxDiffQuestion
      ? [
          {
            description: (
              <>
                {hasMaxDiffQuestion && <div>Max Diff</div>}
                {hasTurfQuestion && <div>TURF</div>}
              </>
            ),
            name: 'Advanced Methodology',
            prices: {
              // each methodology is 300 completes or 1 credit
              completes: hasMaxDiffQuestion && hasTurfQuestion ? 600 : 300,
              credits: hasMaxDiffQuestion && hasTurfQuestion ? 2 : 1,
            },
          },
        ]
      : []),
  ];

  const paymentOption = isPlan ? payWithCompletes : payWithCredits;
  const subTotalCost = getCartCost({ addOns, currency: paymentOption.currency, items });
  const promos =
    !isPlan && usePromoCredits
      ? [
          {
            credits: Math.min(promoCreditsAtReview, subTotalCost),
            name: 'Applied Promotional Credits',
          },
        ]
      : [];
  const totalCost = getCartCost({ addOns, currency: paymentOption.currency, items, promos });

  useEffect(() => {
    if (isPlan && usePromoCredits) {
      setUsePromoCredits(false);
    } else if (!isPlan && usePromoCredits && promoCreditsAtReview < subTotalCost) {
      setUsePromoCredits(false);
    }
  }, [isPlan, totalCost, promoCreditsAtReview, usePromoCredits]);

  const lowestPanelistRatio = Math.min(
    ...survey.groups.map(({ quotaLimit, validationUsers }) => (validationUsers || 0) / quotaLimit)
  );
  const expectedCompletionDays =
    lowestPanelistRatio >= 10 ? '1-2' : lowestPanelistRatio >= 5 ? '2-5' : '3-7';

  useEffect(() => {
    const hasPendingValidation = survey.groups.some(({ validationStatus }) =>
      [NOT_VALIDATED, VALIDATING].includes(validationStatus)
    );

    if (hasPendingValidation) {
      validateSurvey();
    } else {
      setIsValidating(false);
    }

    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!surveyIdRef.current) return;

    const currentValidation = surveysResponsesValidation?.find(
      ({ id }) => id === surveyIdRef.current
    );

    const done =
      currentValidation?.status === 'COMPLETED' || currentValidation?.status === 'FAILED';
    if (!done) return;

    (async () => {
      try {
        const { groups } = await readInstantSurvey({ id: surveyIdRef.current });
        updateSurvey({ groups });
      } catch (err) {
        console.error('Error reading survey:', err);
      } finally {
        surveyIdRef.current = null;
        setIsValidating(false);
      }
    })();
  }, [surveysResponsesValidation, updateSurvey]);

  const validateSurvey = async () => {
    try {
      await validateInstantSurvey({ id: survey.id, signal });
      surveyIdRef.current = survey.id;
    } catch (error) {
      if (!signal.aborted) {
        setValidationError(error);
        setIsValidating(false);
      }
    }
  };

  const getGroupIcon = (group) => {
    if (validationError) return null;
    switch (group.validationStatus) {
      case ERROR:
      case NOT_ENOUGH_SAMPLE:
        return <ExclamationTriangleIcon className={styles['review-group-icon-warning']} />;
      default:
        return <CheckCircleSolidIcon className={styles['review-group-icon']} />;
    }
  };

  const handleReviewAddonChange = (name, changes) => {
    setAddOns((prev) =>
      prev.map((addon) => (addon.name === name ? { ...addon, ...changes } : addon))
    );
  };

  const handleSubmit = async () => {
    if (!survey.title.trim()) {
      setIsTitleRequiredModalOpen(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedAddonValues = addOns
        .filter((addon) => addon?.selected)
        .map((addon) => addon.value);

      await saveInstantSurvey({ signal });

      onPublish(
        await publishAtlasSurvey({
          addons: selectedAddonValues,
          atlasInstantSurveyRef: survey.id,
          id: insightsDocument?.id,
          other_requests: survey.other_requests,
          payment_method: launchMethod,
          status: null,
          title: survey.title,
          use_promo_credits: isPlan ? false : usePromoCredits,
        })
      );
    } catch (err) {
      console.error(err);
      setError(err);
      setIsSubmitting(false);
    }
  };

  const creditsAvailable = usePromoCredits ? promoCreditsAtReview : userCreditsAtReview;
  const completesAvailable = subscription?.currentPeriod?.remainingCompletes ?? 0;

  const isLaunchDisabled =
    isSubmitting ||
    (isPlan && completesAvailable < totalCost) ||
    (!isPlan && creditsAvailable < totalCost) ||
    isResearchObjectivesTooLong;

  if (error) return <Error status={error?.response?.status} />;

  if (isValidating) {
    return (
      <LoaderView heading="Validating Sample Size...">
        <p>
          Just making sure there are enough panelists available to complete this survey, as
          requested. Validation should only take a moment.
        </p>
      </LoaderView>
    );
  }

  return (
    <div className={styles['review']}>
      <div>
        <SurveyToolbar isSaveAsDraftDisabled={isResearchObjectivesTooLong} />
        <br />
        <h2>You're almost ready to launch your survey</h2>
        <p>
          Our team will review your survey for quality control before it is set active for
          panelists. Typically this takes less than 24 business hours.
        </p>
      </div>
      <hr />
      <Layout.Flex className={styles['review-column-layout']} gap="small">
        <Layout.Fill>
          <Card className={styles['review-card']} padding="x-large">
            <div className={styles['review-section']}>
              <h3>{instantSurveyType.name}</h3>
              {isSurveyContinuouslyFed && surveyHasNotEnoughSample ? (
                <p>
                  Continuously Fed Sample:&nbsp;
                  <strong>Up to 90 days</strong>
                </p>
              ) : (
                <p>
                  Estimated Completion:&nbsp;
                  <strong>{expectedCompletionDays} business days</strong>
                </p>
              )}
              <p>We will send an email notification when complete.</p>
            </div>
            <hr />
            <div className={styles['review-section']}>
              <h5>
                <strong>Panelists</strong> (<button onClick={() => setActiveStep(0)}>edit</button>)
              </h5>
              {survey.groups.length > 1 && (
                <p>
                  <strong>{survey.groups.length}</strong> Quota Groups with&nbsp;
                  <strong>{totalResponsesRequested}</strong> Total Responses
                </p>
              )}
              {validationError && (
                <Announcement
                  className={styles['review-announcement']}
                  header={SURVEY_VALIDATION_ERROR_HEADER}
                  text={SURVEY_VALIDATION_ERROR_TEXT}
                  variant="warn"
                />
              )}
              {!validationError && surveyHasNotEnoughSample && (
                <Announcement
                  className={styles['review-announcement']}
                  text={
                    isSurveyContinuouslyFed
                      ? `Your survey may not immediately collect ${totalResponsesRequested} responses based on current sample availability. Responses will continue to collect over time as new sample becomes available.`
                      : `Your survey may not collect ${totalResponsesRequested} total responses. You may modify your prompts to include more panelists.`
                  }
                  variant="warn"
                />
              )}
              <div>
                {survey.groups.length === 1 && (
                  <>
                    {isContinuouslyFed(survey.groups[0]) && (
                      <div className={styles['review-group-badges']}>
                        <Pill isCompleted value="Continuously Fed Sample" />
                      </div>
                    )}
                    <div className={styles['review-group-madlib']}>
                      <GroupMadlib
                        group={survey.groups[0]}
                        isReadOnly
                        surveyType={instantSurveyType}
                      />
                      {(survey.groups[0]?.conceptImage?.cdnUrl ||
                        survey.groups[0]?.conceptName) && (
                        <div className={styles['review-group-concept']}>
                          {survey.groups[0]?.conceptImage?.cdnUrl && (
                            <img alt="Concept File" src={survey.groups[0]?.conceptImage?.cdnUrl} />
                          )}
                          {survey.groups[0]?.conceptName && <p>{survey.groups[0]?.conceptName}</p>}
                        </div>
                      )}
                    </div>
                    {instantSurveyType.advancedPrompts.length > 0 && (
                      <GroupAdvancedPrompts
                        advancedPrompts={instantSurveyType.advancedPrompts}
                        group={survey.groups[0]}
                        isCompleted
                      />
                    )}
                  </>
                )}
                {survey.groups.length > 1 &&
                  survey.groups.map((group) => (
                    <Accordion icon={getGroupIcon(group)} key={group.id} label={group.name}>
                      <div className={styles['review-group']}>
                        {isContinuouslyFed(group) && (
                          <div className={styles['review-group-badges']}>
                            <Pill isCompleted value="Continuously Fed Sample" />
                          </div>
                        )}
                        <div className={styles['review-group-madlib']}>
                          <GroupMadlib group={group} isReadOnly surveyType={instantSurveyType} />
                          {(group?.conceptImage?.cdnUrl || group?.conceptName) && (
                            <div className={styles['review-group-concept']}>
                              {group?.conceptImage?.cdnUrl && (
                                <img alt="Concept File" src={group?.conceptImage?.cdnUrl} />
                              )}
                              {group?.conceptName && <p>{group?.conceptName}</p>}
                            </div>
                          )}
                        </div>
                        {instantSurveyType.advancedPrompts.length > 0 && (
                          <GroupAdvancedPrompts
                            advancedPrompts={instantSurveyType.advancedPrompts}
                            group={group}
                            isCompleted
                          />
                        )}
                        {group.validationStatus !== ERROR && !validationError && (
                          <>
                            <div>
                              <h4>Available Panelists</h4>
                              <p>{formatAvailablePanelists(group)}</p>
                            </div>
                            <div>
                              <h4>Expected Responses</h4>
                              <p>
                                <strong>
                                  {isContinuouslyFed(group) &&
                                  group.validationStatus === NOT_ENOUGH_SAMPLE
                                    ? 'dependent on continuously fed sample'
                                    : formatData({
                                        format: '0,0',
                                        value: Math.min(group.expectedCompletes, group.quotaLimit),
                                      })}
                                </strong>
                              </p>
                            </div>
                          </>
                        )}
                        {group.validationStatus === ERROR && !validationError && (
                          <Announcement
                            header={SURVEY_GROUP_VALIDATION_ERROR_HEADER}
                            text={SURVEY_GROUP_VALIDATION_ERROR_TEXT}
                            variant="warn"
                          />
                        )}
                      </div>
                    </Accordion>
                  ))}
              </div>
              {survey.groups.length === 1 && !validationError && (
                <>
                  {survey.groups[0].validationStatus === ERROR ? (
                    <Announcement
                      className={styles['review-announcement']}
                      header={SURVEY_GROUP_VALIDATION_ERROR_HEADER}
                      text={SURVEY_GROUP_VALIDATION_ERROR_TEXT}
                      variant="warn"
                    />
                  ) : (
                    <>
                      <div>
                        <h4>Available Panelists</h4>
                        <p>{formatAvailablePanelists(survey.groups[0])}</p>
                      </div>
                      <div>
                        <h4>Expected Responses</h4>
                        <p>
                          <strong>
                            {isSurveyContinuouslyFed && surveyHasNotEnoughSample
                              ? 'dependent on continuously fed sample'
                              : formatData({
                                  format: '0,0',
                                  value: Math.min(
                                    survey.groups[0].expectedCompletes,
                                    survey.groups[0].quotaLimit
                                  ),
                                })}
                          </strong>
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className={styles['review-section']}>
              <h5>
                <strong>Questionnaire</strong> (
                <button onClick={() => setActiveStep(1)}>edit</button>)
              </h5>
              <Accordion
                className={styles['review-questions']}
                isDefaultOpen
                label={`${currentQuestionCount} Questions`}
              >
                <ol className={styles['review-question-list']}>
                  {(() => {
                    let questionIndex = 0; // Needed to skip instructional text indices
                    return survey.questions.map((question) => {
                      const questionCost = getQuestionCost(question);
                      const startingIndex = questionIndex + 1;
                      const endingIndex = questionIndex + questionCost;

                      const displayNumber =
                        question.type === QUESTION_TYPES.INSTRUCTIONS.value
                          ? 'T'
                          : `${startingIndex}${endingIndex > startingIndex ? '-' + endingIndex : ''}`;
                      questionIndex = questionIndex + questionCost;

                      return (
                        <li key={question.id}>
                          <Layout.Flex gap="small">
                            <span className={styles['review-question-list-index']}>
                              {displayNumber}.
                            </span>
                            <Layout.Fill>
                              {question.type === QUESTION_TYPES.INSTRUCTIONS.value ? (
                                <em>{question.text}</em>
                              ) : (
                                question.text
                              )}
                            </Layout.Fill>
                            {(survey.questionLogic[question.id]?.skip ||
                              survey.questionLogic[question.id]?.disqualify) && (
                              <span>
                                {survey.questionLogic[question.id]?.skip && (
                                  <Tag
                                    className={styles['review-question-list-tag']}
                                    icon={<FlowArrowIcon />}
                                    tooltip="Skippable"
                                  />
                                )}
                                {survey.questionLogic[question.id]?.disqualify && (
                                  <Tag
                                    className={styles['review-question-list-tag']}
                                    icon={<TargetIcon />}
                                    tooltip="Disqualifier"
                                  />
                                )}
                              </span>
                            )}
                          </Layout.Flex>
                          {question.subQuestions?.length > 0 && (
                            <ol>
                              {question.subQuestions.map(({ id, text }) => (
                                <li key={id}>- {text}</li>
                              ))}
                            </ol>
                          )}
                        </li>
                      );
                    });
                  })()}
                </ol>
              </Accordion>
            </div>
          </Card>
        </Layout.Fill>

        <Layout.Flex.Column className={styles['review-cart']}>
          <Cart
            addOns={addOns}
            hasCaveats={isPlan}
            items={items}
            onAddonChange={handleReviewAddonChange}
            paymentOption={paymentOption}
            promos={promos}
            totalText={isPlan ? 'Completes charged' : 'Total'}
          />

          {isPlan && (
            <Layout.Flex.Column gap="small">
              <Layout.Flex className={styles['review-plan-footnote']} gap="x-small">
                <InfoFilledIcon />
                <Layout.Fill>
                  These completes will be charged on your team’s monthly/quarterly completes. They
                  do not increase the responses you get for this survey.
                </Layout.Fill>
              </Layout.Flex>
              {!isLoadingSubscription && completesAvailable < totalCost && (
                <p className={styles['review-completes-warning']}>
                  Not enough completes to launch.
                  {subscription?.hasFuturePeriods && subscription?.currentPeriod?.endDate
                    ? ` Next quarterly reset is on ${getFormattedLongDate(
                        parseLocalDate(subscription.currentPeriod.endDate)
                      )}.`
                    : ''}{' '}
                  Please contact your Numerator team for more information on completes.
                </p>
              )}
            </Layout.Flex.Column>
          )}

          {!isPlan && (
            <Layout.Flex.Column gap="small">
              <Layout.Flex.Column className={styles['review-credits-card']} gap="small">
                <h4>Credits for {`${firstName} ${lastName}`}</h4>
                <Layout.Flex>
                  <Layout.Fill>
                    <p>Available</p>
                  </Layout.Fill>
                  <span>{`${userCreditsAtReview} ${getDisplayCurrency({
                    paymentOption,
                    value: userCreditsAtReview,
                  })}`}</span>
                </Layout.Flex>
                {creditsAvailable < totalCost && (
                  <p className={styles['review-credits-warning']}>
                    Not enough credits to launch. Please contact your Numerator team for more
                    information on credits.
                  </p>
                )}
              </Layout.Flex.Column>

              {promoCreditsAtReview > 0 && (
                <PromoCredits
                  firstName={firstName}
                  lastName={lastName}
                  onTogglePromoCredits={() => setUsePromoCredits((prev) => !prev)}
                  promoCredits={promoCreditsAtReview}
                  totalCost={totalCost}
                  usePromoCredits={usePromoCredits}
                />
              )}
            </Layout.Flex.Column>
          )}

          <div className={styles['review-actions']}>
            <Button
              data-testid="previous-button"
              onClick={() => setActiveStep(1)}
              text="Previous"
              variant="secondary"
            />
            <Button
              data-testid="launch-button"
              isDisabled={isLaunchDisabled}
              isLoading={isSubmitting}
              onClick={handleSubmit}
              text="Launch"
            />
          </div>
        </Layout.Flex.Column>
      </Layout.Flex>
      {isTitleRequiredModalOpen && (
        <ModalTitleRequired onClose={() => setIsTitleRequiredModalOpen(false)} />
      )}
    </div>
  );
};

Review.propTypes = {
  onPublish: PropTypes.func.isRequired,
  setActiveStep: PropTypes.func.isRequired,
};

export default Review;
